import React, { useCallback, useEffect, useState } from 'react';
import { clsx } from 'clsx';
import { Loader2, Pencil, Plus, Trash2, Wand2 } from 'lucide-react';
import { Button } from '@components/Button';
import { ApiErrorModal } from '@components/ApiErrorModal';
import { ConfirmDialog } from '@components/Dialog';
import { ActionButton } from '@components/Table';
import categoryService from '@services/categoryService';
import importService from '@services/importService';
import { IMPORT_RULE_APPLIES_TO_LABELS, MOVEMENT_LABELS, type Category, type ImportRule } from '@/types/finance';
import { ColorChip } from './financeBits';
import { ImportRuleFormModal } from './ImportRuleFormModal';

/** Finanças → Regras de importação: padrão → categoria sugerida (GET/POST/PUT/DELETE /import-rules). */
export const ImportRulesView: React.FC = () => {
  const [items, setItems] = useState<ImportRule[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<unknown>(null);
  const [form, setForm] = useState<{ rule?: ImportRule } | null>(null);
  const [deleting, setDeleting] = useState<ImportRule | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setItems(await importService.getRules());
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    categoryService.getAll().then(setCategories).catch(setError);
  }, [load]);

  const confirmDelete = async () => {
    if (!deleting) return;
    setBusy(true);
    try {
      await importService.removeRule(deleting.id);
      setDeleting(null);
      load();
    } catch (err) {
      setDeleting(null);
      setError(err);
    } finally {
      setBusy(false);
    }
  };

  const actions = (r: ImportRule) => (
    <div className="flex items-center justify-end gap-0.5">
      <ActionButton icon={<Pencil size={16} />} variant="edit" tooltip="Editar" onClick={() => setForm({ rule: r })} />
      <ActionButton icon={<Trash2 size={16} />} variant="delete" tooltip="Excluir" onClick={() => setDeleting(r)} />
    </div>
  );

  const status = (r: ImportRule) => (r.is_active ? <span className="badge-success">Ativa</span> : <span className="badge-neutral">Inativa</span>);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-h3 flex items-center gap-2">
          <Wand2 size={20} className="text-slate-500" /> Regras de importação
        </h1>
        <Button variant="primary" size="sm" icon={<Plus />} onClick={() => setForm({})}>
          Nova regra
        </Button>
      </div>

      <p className="text-small">
        Quando a <strong>descrição</strong> de uma linha do extrato contém o <strong>padrão</strong>, a importação sugere a <strong>categoria</strong>. As regras
        são testadas em ordem de prioridade (menor primeiro; empate: padrão mais longo) e só valem para o movimento indicado. A categoria escolhida à mão na
        prévia sempre prevalece.
      </p>

      {/* Celular: cartões */}
      <div className="md:hidden space-y-2">
        {loading && items.length === 0 && (
          <p className="text-center text-slate-400 py-6">
            <Loader2 size={18} className="inline animate-spin mr-2" /> Carregando…
          </p>
        )}
        {!loading && items.length === 0 && <p className="text-center text-slate-500 py-8">Nenhuma regra. Crie aqui ou direto da prévia de uma importação.</p>}
        {items.map((r) => (
          <div key={r.id} className={clsx('card p-3 space-y-1', loading && 'opacity-60', !r.is_active && 'opacity-60')}>
            <div className="flex items-start justify-between gap-2">
              <p className="font-medium text-slate-800 [overflow-wrap:anywhere]">"{r.pattern}"</p>
              {status(r)}
            </div>
            <p className="text-sm">
              <ColorChip color={r.category_color}>{r.category_name}</ColorChip>
            </p>
            <div className="flex flex-wrap items-center justify-between gap-2 text-small">
              <span>
                {IMPORT_RULE_APPLIES_TO_LABELS[r.applies_to]} · {r.movement ? MOVEMENT_LABELS[r.movement] : 'qualquer movimento'} · prioridade {r.priority}
              </span>
              {actions(r)}
            </div>
          </div>
        ))}
      </div>

      {/* Tabela */}
      <div className="table-container bg-white hidden md:block">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="table-header">
              <tr>
                <th>Padrão</th>
                <th>Categoria</th>
                <th>Aplica-se a</th>
                <th>Movimento</th>
                <th>Prioridade</th>
                <th>Situação</th>
                <th className="w-24"></th>
              </tr>
            </thead>
            <tbody>
              {loading && items.length === 0 && (
                <tr>
                  <td colSpan={7} className="table-cell text-center text-slate-400">
                    <Loader2 size={18} className="inline animate-spin mr-2" /> Carregando…
                  </td>
                </tr>
              )}
              {!loading && items.length === 0 && (
                <tr>
                  <td colSpan={7} className="table-cell text-center py-12 text-slate-500">
                    Nenhuma regra. Crie aqui ou direto da prévia de uma importação.
                  </td>
                </tr>
              )}
              {items.map((r) => (
                <tr key={r.id} className={clsx('table-row', (loading || !r.is_active) && 'opacity-60')}>
                  <td className="table-cell font-medium text-slate-800 [overflow-wrap:anywhere]">"{r.pattern}"</td>
                  <td className="table-cell">
                    <ColorChip color={r.category_color}>{r.category_name}</ColorChip>
                  </td>
                  <td className="table-cell">{IMPORT_RULE_APPLIES_TO_LABELS[r.applies_to]}</td>
                  <td className="table-cell">{r.movement ? MOVEMENT_LABELS[r.movement] : <span className="text-slate-400">qualquer</span>}</td>
                  <td className="table-cell tabular-nums">{r.priority}</td>
                  <td className="table-cell">{status(r)}</td>
                  <td className="table-cell">{actions(r)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {form && (
        <ImportRuleFormModal
          rule={form.rule}
          categories={categories}
          onClose={() => setForm(null)}
          onSaved={() => {
            setForm(null);
            load();
          }}
          onError={setError}
        />
      )}

      <ConfirmDialog
        isOpen={!!deleting}
        onClose={() => setDeleting(null)}
        onConfirm={confirmDelete}
        title="Excluir regra"
        message={`Excluir a regra "${deleting?.pattern ?? ''}"? As próximas importações deixam de sugerir ${deleting?.category_name ?? 'a categoria'} por ela.`}
        confirmText="Excluir"
        severity="danger"
        loading={busy}
      />

      <ApiErrorModal error={error} onClose={() => setError(null)} />
    </div>
  );
};
